// Service API pour communiquer avec le backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_SERVER_URL = process.env.REACT_APP_API_SERVER_URL || 'http://localhost:3001';

// Fonction utilitaire pour construire l'URL complète d'un fichier uploadé
export const getFileUrl = (filePath) => {
  if (!filePath) return null;
  // Si c'est déjà une URL complète (commence par http:// ou https://), la retourner telle quelle
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }
  // Sinon, construire l'URL complète avec l'URL du serveur
  return `${API_SERVER_URL}${filePath.startsWith('/') ? filePath : `/${filePath}`}`;
};

// Fonction utilitaire pour extraire l'ID d'une vidéo YouTube
export const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  // Format: https://www.youtube.com/watch?v=VIDEO_ID
  // Format: https://youtu.be/VIDEO_ID
  // Format: https://www.youtube.com/embed/VIDEO_ID
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&\n?#]+)/);
  return match ? match[1] : null;
};

// Fonction utilitaire pour convertir une URL YouTube en format embed
export const convertYouTubeUrl = (url) => {
  if (!url) return null;
  
  // Si c'est déjà au format embed, retourner tel quel
  if (url.includes('youtube.com/embed/')) {
    return url;
  }
  
  // Extraire l'ID de la vidéo
  const videoId = getYouTubeVideoId(url);
  
  // Si on a trouvé un ID, construire l'URL embed
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }
  
  // Si on ne peut pas convertir, retourner l'URL originale
  return url;
};

// Fonction utilitaire pour obtenir le thumbnail YouTube
export const getYouTubeThumbnail = (url) => {
  if (!url) return null;
  
  const videoId = getYouTubeVideoId(url);
  if (videoId) {
    // Utiliser maxresdefault pour la meilleure qualité, avec fallback sur hqdefault
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  }
  
  return null;
};

// Fonction utilitaire pour faire des requêtes
const fetchAPI = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

// Fonction utilitaire pour uploader des fichiers
const uploadFile = async (endpoint, file, fieldName = 'file') => {
  try {
    const formData = new FormData();
    formData.append(fieldName, file);

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

// ========== NEWS ==========
export const getNews = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.author_id) queryParams.append('author_id', filters.author_id);

    const queryString = queryParams.toString();
    const endpoint = `/news${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetchAPI(endpoint);
    return response.success ? response.data : [];
  } catch (error) {
    return [];
  }
};

export const getNewsById = async (id) => {
  try {
    const response = await fetchAPI(`/news/${id}`);
    return response.success ? response.data : null;
  } catch (error) {
    return null;
  }
};

export const getNewsArchives = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);

    const queryString = queryParams.toString();
    const endpoint = `/news/archives${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetchAPI(endpoint);
    return response.success ? response.data : [];
  } catch (error) {
    return [];
  }
};

export const getNewsCategories = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);

    const queryString = queryParams.toString();
    const endpoint = `/news/categories${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetchAPI(endpoint);
    return response.success ? response.data : [];
  } catch (error) {
    return [];
  }
};

// ========== CATEGORIES (CRUD) ==========
export const getCategories = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    const queryString = queryParams.toString();
    const endpoint = `/categories${queryString ? `?${queryString}` : ''}`;

    const response = await fetchAPI(endpoint);
    return response.success ? response.data : [];
  } catch (error) {
    return [];
  }
};

export const createCategory = async (categoryData) => {
  try {
    const response = await fetchAPI('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
    return response.success ? response.data : null;
  } catch (error) {
    throw error;
  }
};

export const updateCategory = async (id, categoryData) => {
  try {
    const response = await fetchAPI(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    });
    return response.success ? response.data : null;
  } catch (error) {
    throw error;
  }
};

export const deleteCategory = async (id) => {
  try {
    const response = await fetchAPI(`/categories/${id}`, {
      method: 'DELETE',
    });
    return response.success;
  } catch (error) {
    throw error;
  }
};

// Créer une news
export const createNews = async (newsData) => {
  try {
    const response = await fetchAPI('/news', {
      method: 'POST',
      body: JSON.stringify(newsData)
    });
    return response.success ? response.data : null;
  } catch (error) {
    throw error;
  }
};

// Mettre à jour une news
export const updateNews = async (id, newsData) => {
  try {
    const response = await fetchAPI(`/news/${id}`, {
      method: 'PUT',
      body: JSON.stringify(newsData)
    });
    return response.success ? response.data : null;
  } catch (error) {
    throw error;
  }
};

// Supprimer une news
export const deleteNews = async (id) => {
  try {
    const response = await fetchAPI(`/news/${id}`, {
      method: 'DELETE'
    });
    return response.success;
  } catch (error) {
    throw error;
  }
};

// ========== EVENTS ==========
export const getEvents = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.date_from) queryParams.append('date_from', filters.date_from);
    if (filters.date_to) queryParams.append('date_to', filters.date_to);

    const queryString = queryParams.toString();
    const endpoint = `/events${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetchAPI(endpoint);
    return response.success ? response.data : [];
  } catch (error) {
    return [];
  }
};

export const getEventById = async (id) => {
  try {
    const response = await fetchAPI(`/events/${id}`);
    return response.success ? response.data : null;
  } catch (error) {
    return null;
  }
};

export const getEventArchives = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);

    const queryString = queryParams.toString();
    const endpoint = `/events/archives${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetchAPI(endpoint);
    return response.success ? response.data : [];
  } catch (error) {
    return [];
  }
};

// Créer un événement
export const createEvent = async (eventData) => {
  try {
    const response = await fetchAPI('/events', {
      method: 'POST',
      body: JSON.stringify(eventData)
    });
    return response.success ? response.data : null;
  } catch (error) {
    throw error;
  }
};

// Mettre à jour un événement
export const updateEvent = async (id, eventData) => {
  try {
    const response = await fetchAPI(`/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(eventData)
    });
    return response.success ? response.data : null;
  } catch (error) {
    throw error;
  }
};

// Supprimer un événement
export const deleteEvent = async (id) => {
  try {
    const response = await fetchAPI(`/events/${id}`, {
      method: 'DELETE'
    });
    return response.success;
  } catch (error) {
    throw error;
  }
};

// ========== RESOURCES ==========
export const getResources = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.category) queryParams.append('category', filters.category);

    const queryString = queryParams.toString();
    const endpoint = `/resources${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetchAPI(endpoint);
    return response.success ? response.data : [];
  } catch (error) {
    return [];
  }
};

export const getResourceById = async (id) => {
  try {
    const response = await fetchAPI(`/resources/${id}`);
    return response.success ? response.data : null;
  } catch (error) {
    return null;
  }
};

// Créer une ressource
export const createResource = async (resourceData) => {
  try {
    const response = await fetchAPI('/resources', {
      method: 'POST',
      body: JSON.stringify(resourceData)
    });
    return response.success ? response.data : null;
  } catch (error) {
    throw error;
  }
};

// Mettre à jour une ressource
export const updateResource = async (id, resourceData) => {
  try {
    const response = await fetchAPI(`/resources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(resourceData)
    });
    return response.success ? response.data : null;
  } catch (error) {
    throw error;
  }
};

// Supprimer une ressource
export const deleteResource = async (id) => {
  try {
    const response = await fetchAPI(`/resources/${id}`, {
      method: 'DELETE'
    });
    return response.success;
  } catch (error) {
    throw error;
  }
};

// Incrémenter les téléchargements
export const incrementResourceDownload = async (id) => {
  try {
    const response = await fetchAPI(`/resources/${id}/download`, {
      method: 'POST'
    });
    return response.success;
  } catch (error) {
    throw error;
  }
};

// ========== COMMENTS ==========
export const getComments = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.news_id) queryParams.append('news_id', filters.news_id);
    if (filters.event_id) queryParams.append('event_id', filters.event_id);
    if (filters.include_replies) queryParams.append('include_replies', 'true');

    const queryString = queryParams.toString();
    const endpoint = `/comments${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetchAPI(endpoint);
    return response.success ? response.data : [];
  } catch (error) {
    return [];
  }
};

export const getCommentById = async (id) => {
  try {
    const response = await fetchAPI(`/comments/${id}`);
    return response.success ? response.data : null;
  } catch (error) {
    return null;
  }
};

// Créer un commentaire
export const createComment = async (commentData) => {
  try {
    const response = await fetchAPI('/comments', {
      method: 'POST',
      body: JSON.stringify(commentData)
    });
    return response.success ? response.data : null;
  } catch (error) {
    throw error;
  }
};

// Mettre à jour un commentaire
export const updateComment = async (id, commentData) => {
  try {
    const response = await fetchAPI(`/comments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(commentData)
    });
    return response.success ? response.data : null;
  } catch (error) {
    throw error;
  }
};

// Supprimer un commentaire
export const deleteComment = async (id) => {
  try {
    const response = await fetchAPI(`/comments/${id}`, {
      method: 'DELETE'
    });
    return response.success;
  } catch (error) {
    throw error;
  }
};

// Toggle like sur un commentaire
export const toggleCommentLike = async (id) => {
  try {
    const response = await fetchAPI(`/comments/${id}/like`, {
      method: 'POST'
    });
    return response.success ? response : null;
  } catch (error) {
    throw error;
  }
};

// Vérifier si un utilisateur a liké un commentaire
export const checkCommentLike = async (id) => {
  try {
    const response = await fetchAPI(`/comments/${id}/like`);
    return response.success ? response : null;
  } catch (error) {
    return { liked: false, likes_count: 0 };
  }
};

// Marquer tous les commentaires comme lus
export const markAllCommentsAsRead = async () => {
  try {
    const response = await fetchAPI('/comments/mark-all-read', {
      method: 'POST'
    });
    return response.success;
  } catch (error) {
    throw error;
  }
};

// ========== AUTH ==========
// Connexion utilisateur
export const login = async (email, password) => {
  try {
    const response = await fetch(`${API_SERVER_URL}/api/users/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erreur de connexion');
    }

    const data = await response.json();
    return data.success ? data : null;
  } catch (error) {
    throw error;
  }
};

// ========== USERS ==========
// Récupérer un utilisateur par ID
export const getUserById = async (id) => {
  try {
    const response = await fetchAPI(`/users/${id}`);
    return response.success ? response.data : null;
  } catch (error) {
    throw error;
  }
};

// Mettre à jour un utilisateur
export const updateUser = async (id, userData) => {
  try {
    const response = await fetchAPI(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
    return response.success ? response.data : null;
  } catch (error) {
    throw error;
  }
};

// ========== DASHBOARD ==========
// Récupérer les statistiques du dashboard
export const getDashboardStats = async () => {
  try {
    const response = await fetchAPI('/dashboard/stats');
    return response.success ? response.data : null;
  } catch (error) {
    return null;
  }
};

// Récupérer les notifications
export const getNotifications = async () => {
  try {
    const response = await fetchAPI('/dashboard/notifications');
    return response.success ? response.data : null;
  } catch (error) {
    return null;
  }
};

// ========== UPLOAD ==========
// Uploader une image
export const uploadImage = async (file) => {
  try {
    const response = await uploadFile('/upload/image', file, 'image');
    return response.success ? response.file : null;
  } catch (error) {
    throw error;
  }
};

// Uploader une vidéo
export const uploadVideo = async (file) => {
  try {
    const response = await uploadFile('/upload/video', file, 'video');
    return response.success ? response.file : null;
  } catch (error) {
    throw error;
  }
};

// Uploader un fichier de ressource (tous types)
export const uploadResourceFile = async (file) => {
  try {
    const response = await uploadFile('/upload/resource', file, 'file');
    return response.success ? response.file : null;
  } catch (error) {
    throw error;
  }
};

// Uploader plusieurs médias
export const uploadMedia = async (files) => {
  try {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('media', file);
    });

    const response = await fetch(`${API_BASE_URL}/upload/media`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      // Essayer de récupérer le message d'erreur du serveur
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (e) {
        // Si on ne peut pas parser la réponse, utiliser le message par défaut
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Erreur lors de l\'upload des médias');
    }
    return data.files || [];
  } catch (error) {
    throw error;
  }
};

// ========== CONTACT ==========
// Envoyer un email de contact
export const sendContactEmail = async (contactData) => {
  try {
    const { name, email, subject, message } = contactData;

    if (!name || !email || !subject || !message) {
      throw new Error('Tous les champs sont requis');
    }

    const response = await fetch(`${API_BASE_URL}/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name, email, subject, message })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erreur lors de l\'envoi de l\'email');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

// ========== SETTINGS ==========
// Récupérer les paramètres
export const getSettings = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/settings`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erreur lors de la récupération des paramètres');
    }

    const data = await response.json();
    return data.success ? data.data : null;
  } catch (error) {
    throw error;
  }
};

// Mettre à jour les paramètres
export const updateSettings = async (settingsData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settingsData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erreur lors de la mise à jour des paramètres');
    }

    const data = await response.json();
    return data.success ? data.data : null;
  } catch (error) {
    throw error;
  }
};

// ========== WORK PACKAGES ==========
export const getWorkPackages = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);

    const queryString = queryParams.toString();
    const endpoint = `/workpackages${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetchAPI(endpoint);
    return response.success ? response.data : [];
  } catch (error) {
    console.error('Erreur lors de la récupération des work packages:', error);
    return [];
  }
};

export const getWorkPackageById = async (id) => {
  try {
    const response = await fetchAPI(`/workpackages/${id}`);
    return response.success ? response.data : null;
  } catch (error) {
    console.error('Erreur lors de la récupération du work package:', error);
    return null;
  }
};

export const getWorkPackageByWpId = async (wpId) => {
  try {
    const response = await fetchAPI(`/workpackages/wp/${wpId}`);
    return response.success ? response.data : null;
  } catch (error) {
    console.error('Erreur lors de la récupération du work package:', error);
    return null;
  }
};

// ========== DELIVERABLES ==========
export const getDeliverables = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (filters.wp) queryParams.append('wp', filters.wp);
    if (filters.status) queryParams.append('status', filters.status);

    const queryString = queryParams.toString();
    const endpoint = `/deliverables${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetchAPI(endpoint);
    return response.success ? response.data : [];
  } catch (error) {
    console.error('Erreur lors de la récupération des deliverables:', error);
    return [];
  }
};

export const getDeliverablesByWp = async (wp) => {
  try {
    const response = await fetchAPI(`/deliverables/wp/${wp}`);
    return response.success ? response.data : [];
  } catch (error) {
    console.error('Erreur lors de la récupération des deliverables:', error);
    return [];
  }
};

export const getDeliverableById = async (id) => {
  try {
    const response = await fetchAPI(`/deliverables/${id}`);
    return response.success ? response.data : null;
  } catch (error) {
    console.error('Erreur lors de la récupération du deliverable:', error);
    return null;
  }
};

// ========== PARTNERS ==========
export const getPartners = async () => {
  try {
    const response = await fetchAPI('/partners');
    return response.success ? response.data : [];
  } catch (error) {
    console.error('Erreur lors de la récupération des partenaires:', error);
    return [];
  }
};

export const createPartner = async (data) => {
  try {
    const response = await fetchAPI('/partners', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response;
  } catch (error) {
    console.error('Erreur lors de la création du partenaire:', error);
    throw error;
  }
};

export const updatePartner = async (id, data) => {
  try {
    const response = await fetchAPI(`/partners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return response;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du partenaire:', error);
    throw error;
  }
};

export const deletePartner = async (id) => {
  try {
    const response = await fetchAPI(`/partners/${id}`, {
      method: 'DELETE'
    });
    return response;
  } catch (error) {
    console.error('Erreur lors de la suppression du partenaire:', error);
    throw error;
  }
};

export const createDeliverable = async (deliverableData) => {
  try {
    const response = await fetchAPI('/deliverables', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(deliverableData)
    });
    return response.success ? response.data : null;
  } catch (error) {
    console.error('Erreur lors de la création du deliverable:', error);
    throw error;
  }
};

export const updateDeliverable = async (id, deliverableData) => {
  try {
    const response = await fetchAPI(`/deliverables/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(deliverableData)
    });
    return response.success ? response.data : null;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du deliverable:', error);
    throw error;
  }
};

export const deleteDeliverable = async (id) => {
  try {
    const response = await fetchAPI(`/deliverables/${id}`, {
      method: 'DELETE'
    });
    return response.success;
  } catch (error) {
    console.error('Erreur lors de la suppression du deliverable:', error);
    throw error;
  }
};

export default {
  getNews,
  getNewsById,
  getNewsArchives,
  getNewsCategories,
  createNews,
  updateNews,
  deleteNews,
  getEvents,
  getEventById,
  getEventArchives,
  createEvent,
  updateEvent,
  deleteEvent,
  getResources,
  getResourceById,
  getComments,
  sendContactEmail,
  getSettings,
  updateSettings,
  getWorkPackages,
  getWorkPackageById,
  getWorkPackageByWpId,
  getDeliverables,
  getDeliverablesByWp,
  getDeliverableById,
  createDeliverable,
  updateDeliverable,
  deleteDeliverable
};

